import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../lib/auth';
import { api } from '../lib/api';
import { 
  LayoutDashboard, Users, LogOut, Phone, UserCircle, Menu, X, Clock, 
  Link as LinkIcon, UsersRound, FileStack, ChevronDown, ChevronRight,
  Headphones, Settings, MessageSquare, AlertTriangle, Tag, Plus, TrendingUp,
  Package, Trophy, BookOpen, Calendar, GraduationCap, ListChecks, Key,
  Warehouse, ArrowRightLeft, ClipboardList, Wrench, Shield, DollarSign, Building2,
  CalendarCheck, UserCheck, FileBarChart, FileText, Palette, Archive, Layers, PenTool,
  HelpCircle, FolderTree, PlayCircle, BarChart3, Sparkles, Receipt, Share2, ExternalLink, Youtube, Star, IndianRupee
} from 'lucide-react';
import NotificationDropdown from './NotificationDropdown';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(authService.getCurrentUser());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [salesCallingOpen, setSalesCallingOpen] = useState(false);
  const [complaintsOpen, setComplaintsOpen] = useState(false);
  const [classDeliveryOpen, setClassDeliveryOpen] = useState(false);
  const [typingOpen, setTypingOpen] = useState(false);
  const [contentOpen, setContentOpen] = useState(false);
  const [booksStockOpen, setBooksStockOpen] = useState(false);
  const [hrOpen, setHrOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [offlineOpen, setOfflineOpen] = useState(false);
  const [specialEventsOpen, setSpecialEventsOpen] = useState(false);
  const [cashFlowOpen, setCashFlowOpen] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);
  const [isTeamLeader, setIsTeamLeader] = useState(false);

  // Refresh user data to get latest module_access
  useEffect(() => {
    authService.refreshUser().then(updated => {
      if (updated) setUser(updated);
    });
  }, []);

  // Check if user is a team leader
  useEffect(() => {
    const checkTeamLeaderStatus = async () => {
      try {
        const res = await api.checkIsTeamLeader();
        setIsTeamLeader(res.data.is_team_leader);
      } catch (error) {
        console.error('Error checking team leader status:', error);
        setIsTeamLeader(false);
      }
    };
    
    if (user) {
      checkTeamLeaderStatus();
    }
  }, [user]);

  // Helper function to check if user has access to a module
  const hasModuleAccess = (module) => {
    // Admin has access to everything
    if (user?.role === 'admin') return true;
    
    // For managers, check module_access array
    // If module_access is empty or not set, they have access to ALL modules (default behavior)
    // If module_access has specific values, only show those modules
    if (user?.role === 'manager') {
      const moduleAccess = user.module_access || [];
      // Empty array = access to all modules
      if (moduleAccess.length === 0) return true;
      
      // Check multiple possible names for each module
      if (module === 'sales') {
        return moduleAccess.includes('sales') || moduleAccess.includes('sales_calling');
      }
      if (module === 'complaints') {
        return moduleAccess.includes('complaints')
          || moduleAccess.includes('complaint_entry')
          || moduleAccess.includes('complaint_solving');
      }
      if (module === 'complaint_entry') {
        return moduleAccess.includes('complaint_entry') || moduleAccess.includes('complaints');
      }
      if (module === 'complaint_solving') {
        return moduleAccess.includes('complaint_solving') || moduleAccess.includes('complaints');
      }
      if (module === 'class_delivery') {
        return moduleAccess.includes('class_delivery') || moduleAccess.includes('classes');
      }
      if (module === 'books_stock') {
        return moduleAccess.includes('books_stock');
      }
      if (module === 'hr' || module === 'hr_finance') {
        return moduleAccess.includes('hr') || moduleAccess.includes('hr_finance');
      }
      if (module === 'finance') {
        return moduleAccess.includes('finance') || moduleAccess.includes('hr_finance');
      }
      if (module === 'offline_centre') {
        return moduleAccess.includes('offline_centre') || moduleAccess.includes('offline');
      }
      if (module === 'special_events') {
        return moduleAccess.includes('special_events');
      }
      if (module === 'results') {
        return moduleAccess.includes('results');
      }
      if (module === 'ecommerce') {
        return moduleAccess.includes('ecommerce');
      }
      if (module === 'marketing') {
        return moduleAccess.includes('marketing');
      }
      if (module === 'doubt_engine') {
        return moduleAccess.includes('doubt_engine');
      }
      if (module === 'teacher_feedback') {
        return moduleAccess.includes('teacher_feedback');
      }
      if (module === 'complaint_chat') {
        return moduleAccess.includes('complaint_chat')
          || moduleAccess.includes('complaint_entry')
          || moduleAccess.includes('complaint_solving');
      }
      return moduleAccess.includes(module);
    }
    
    // For specific roles, also check module_access array first
    const userModuleAccess = user?.module_access || [];
    if (userModuleAccess.includes(module)) return true;
    // Special aliases
    if (module === 'special_events' && userModuleAccess.includes('special_events')) return true;
    if (module === 'offline_centre' && (userModuleAccess.includes('offline_centre') || userModuleAccess.includes('offline'))) return true;
    
    // Fallback: check by role
    if (module === 'sales') {
      return ['caller'].includes(user?.role);
    }
    if (module === 'complaints') {
      return ['complaint_entry', 'complaint_solver'].includes(user?.role);
    }
    if (module === 'class_delivery') {
      return ['class_conduction_agent', 'teacher'].includes(user?.role);
    }
    if (module === 'books_stock') {
      return ['stock_manager'].includes(user?.role);
    }
    if (module === 'hr') {
      return ['hr_admin', 'hr_manager'].includes(user?.role);
    }
    if (module === 'finance') {
      return ['finance_admin'].includes(user?.role);
    }
    if (module === 'offline_centre') {
      return ['offline_manager', 'counsellor'].includes(user?.role);
    }
    if (module === 'special_events') {
      return ['special_events_agent'].includes(user?.role);
    }
    if (module === 'results') {
      return ['result_agent'].includes(user?.role);
    }
    if (module === 'marketing') {
      return ['marketing_agent'].includes(user?.role);
    }
    if (module === 'doubt_engine') {
      return ['doubt_engine_manager', 'teacher'].includes(user?.role);
    }
    if (module === 'teacher_feedback') {
      return ['feedback_manager', 'teacher'].includes(user?.role);
    }
    
    return false;
  };

  // Check if current path is under sales calling, complaints, class delivery, content, or books stock
  useEffect(() => {
    if (location.pathname.startsWith('/sales')) {
      setSalesCallingOpen(true);
      setComplaintsOpen(false);
      setClassDeliveryOpen(false);
      setContentOpen(false);
      setBooksStockOpen(false);
    } else if (location.pathname.startsWith('/complaints') || location.pathname.startsWith('/complaint-chat')) {
      setComplaintsOpen(true);
      setSalesCallingOpen(false);
      setClassDeliveryOpen(false);
      setContentOpen(false);
      setBooksStockOpen(false);
    } else if (location.pathname.startsWith('/classes')) {
      setClassDeliveryOpen(true);
      setSalesCallingOpen(false);
      setComplaintsOpen(false);
      setContentOpen(false);
      setBooksStockOpen(false);
    } else if (location.pathname.startsWith('/content')) {
      setContentOpen(true);
      setSalesCallingOpen(false);
      setComplaintsOpen(false);
      setClassDeliveryOpen(false);
      setBooksStockOpen(false);
      setHrOpen(false);
      setFinanceOpen(false);
    } else if (location.pathname.startsWith('/stock')) {
      setBooksStockOpen(true);
      setSalesCallingOpen(false);
      setComplaintsOpen(false);
      setClassDeliveryOpen(false);
      setContentOpen(false);
      setHrOpen(false);
      setFinanceOpen(false);
    } else if (location.pathname.startsWith('/hr')) {
      setHrOpen(true);
      setSalesCallingOpen(false);
      setComplaintsOpen(false);
      setClassDeliveryOpen(false);
      setContentOpen(false);
      setBooksStockOpen(false);
      setFinanceOpen(false);
    } else if (location.pathname.startsWith('/finance')) {
      setFinanceOpen(true);
      setSalesCallingOpen(false);
      setComplaintsOpen(false);
      setClassDeliveryOpen(false);
      setContentOpen(false);
      setBooksStockOpen(false);
      setHrOpen(false);
      setOfflineOpen(false);
    } else if (location.pathname.startsWith('/offline')) {
      setOfflineOpen(true);
      setSalesCallingOpen(false);
      setComplaintsOpen(false);
      setClassDeliveryOpen(false);
      setContentOpen(false);
      setBooksStockOpen(false);
      setHrOpen(false);
      setFinanceOpen(false);
    } else if (location.pathname.startsWith('/results')) {
      setResultsOpen(true);
    } else if (location.pathname.startsWith('/marketing')) {
      setMarketingOpen(true);
    } else if (location.pathname.startsWith('/cashflow')) {
      setCashFlowOpen(true);
    }
  }, [location.pathname]);

  // Send heartbeat every 30 seconds to keep user online
  useEffect(() => {
    if (!user) return;
    
    api.heartbeat().catch(console.error);
    
    const heartbeatInterval = setInterval(() => {
      api.heartbeat().catch(console.error);
    }, 30000);
    
    return () => clearInterval(heartbeatInterval);
  }, [user]);

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    authService.logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isSalesActive = () => {
    return location.pathname.startsWith('/sales');
  };

  const isComplaintsActive = () => {
    return location.pathname.startsWith('/complaints') || location.pathname.startsWith('/complaint-chat');
  };

  // Main navigation items - Dashboard only for admin
  const mainNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin'] },
  ];

  // Sales Calling sub-navigation items
  const salesNavItems = [
    { path: '/sales/dashboard', label: 'Sales Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager'] },
    { path: '/sales/my-performance', label: 'My Performance', icon: TrendingUp, roles: ['caller'] },
    { path: '/sales/leads', label: 'Leads', icon: Phone, roles: ['admin', 'manager', 'caller'] },
    { path: '/sales/unused-leads', label: 'Unused Leads', icon: AlertTriangle, roles: ['admin', 'manager'] },
    { path: '/sales/reminders', label: 'Reminders', icon: Clock, roles: ['caller'] },
    { path: '/sales/users', label: 'Users', icon: Users, roles: ['admin', 'manager'] },
    { path: '/sales/caller-groups', label: 'Caller Groups', icon: UsersRound, roles: ['admin', 'manager'] },
    { path: '/sales/lead-groups', label: 'Lead Groups', icon: FileStack, roles: ['admin', 'manager'] },
    { path: '/sales/products', label: 'Products', icon: Package, roles: ['admin', 'manager'] },
    { path: '/sales/lead-forms', label: 'Lead Forms', icon: ClipboardList, roles: ['admin', 'manager'] },
    { path: '/sales/competitions', label: 'Competitions', icon: Trophy, roles: ['admin', 'manager'] },
    { path: '/sales/leaderboard', label: 'Leaderboard', icon: TrendingUp, roles: ['admin', 'manager', 'caller'] },
    { path: '/sales/google-sheet-sync', label: 'Google Sheet Sync', icon: LinkIcon, roles: ['admin', 'manager'] },
  ];

  // Complaints sub-navigation items
  const complaintsNavItems = [
    { path: '/complaints/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager'] },
    { path: '/complaints/my-performance', label: 'My Performance', icon: TrendingUp, roles: ['complaint_entry', 'complaint_solver'] },
    { path: '/complaint-chat/agent', label: 'Live Chat Inbox', icon: MessageSquare, roles: ['admin', 'manager', 'complaint_entry', 'complaint_solver'] },
    { path: '/complaint-chat', label: 'Live Chat Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager'] },
    { path: '/complaints/new', label: 'New Complaint', icon: Plus, roles: ['admin', 'manager', 'complaint_entry'] },
    { path: '/complaints/list', label: 'All Complaints', icon: MessageSquare, roles: ['admin', 'manager', 'complaint_entry', 'complaint_solver'] },
    { path: '/complaints/problem-types', label: 'Problem Types', icon: Tag, roles: ['admin'] },
    { path: '/complaints/public-form', label: 'Public Form', icon: ExternalLink, roles: ['admin', 'manager'] },
  ];

  // Class Delivery sub-navigation items
  const classDeliveryNavItems = [
    { path: '/classes/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'class_conduction_agent'] },
    { path: '/classes/my-performance', label: 'My Performance', icon: TrendingUp, roles: ['teacher'] },
    { path: '/classes/app', label: 'App Classes', icon: ListChecks, roles: ['admin', 'manager', 'class_conduction_agent'] },
    { path: '/classes/youtube', label: 'YouTube Classes', icon: PlayCircle, roles: ['admin', 'manager', 'class_conduction_agent'] },
    { path: '/classes/youtube/by-channel', label: 'YouTube by Channel', icon: Youtube, roles: ['admin', 'manager', 'class_conduction_agent'] },
    { path: '/classes/teachers', label: 'Teachers', icon: GraduationCap, roles: ['admin', 'manager'] },
    { path: '/classes/subjects', label: 'Subjects', icon: FolderTree, roles: ['admin', 'manager'] },
    { path: '/classes/reports', label: 'Reports', icon: FileBarChart, roles: ['admin', 'manager'] },
    { path: '/classes/progress', label: 'Course Progress', icon: BarChart3, roles: ['admin', 'manager', 'class_conduction_agent', 'teacher'] },
  ];

  // Books Stock sub-navigation items
  const booksStockNavItems = [
    { path: '/stock/inward-outward', label: 'Inward/Outward', icon: ArrowRightLeft, roles: ['admin', 'manager', 'stock_manager'] },
    { path: '/stock/ledger', label: 'Book Ledger', icon: BookOpen, roles: ['admin', 'manager', 'stock_manager'] },
    { path: '/stock/status', label: 'Stock Status', icon: ClipboardList, roles: ['admin', 'manager', 'stock_manager'] },
    { path: '/stock/modify', label: 'Modify', icon: Wrench, roles: ['admin', 'manager', 'stock_manager'] },
    // Paper Stock removed
  ];

  // HR sub-navigation items - Admin and Managers with HR access
  const hrNavItems = [
    { path: '/hr/dashboard', label: 'HR Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'hr_manager'] },
    { path: '/hr/employees', label: 'Employees', icon: Users, roles: ['admin', 'manager', 'hr_manager'] },
    { path: '/hr/documents', label: 'Documents', icon: FileStack, roles: ['admin', 'manager', 'hr_manager'] },
    { path: '/hr/salary', label: 'Salary', icon: DollarSign, roles: ['admin', 'manager', 'hr_manager'] },
    { path: '/hr/attendance/teams', label: 'Attendance Teams', icon: UsersRound, roles: ['admin', 'manager', 'hr_manager'] },
    { path: '/hr/attendance/reports', label: 'Attendance Reports', icon: FileBarChart, roles: ['admin', 'manager', 'hr_manager'] },
  ];

  // Attendance Marking - available for Team Leaders (any role can be a team leader)
  const attendanceMarkingItem = { path: '/hr/attendance/mark', label: 'Mark Attendance', icon: CalendarCheck };

  // Finance sub-navigation items - Admin and Managers with Finance access
  const financeNavItems = [
    { path: '/finance/dashboard', label: 'Finance Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager'] },
  ];

  // Offline Centre sub-navigation items
  const offlineCentreNavItems = [
    { path: '/offline/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'offline_manager', 'counsellor'] },
    { path: '/offline/enquiries', label: 'Enquiries', icon: Phone, roles: ['admin', 'manager', 'offline_manager', 'counsellor'] },
    { path: '/offline/admissions', label: 'Admissions', icon: UserCheck, roles: ['admin', 'manager', 'offline_manager', 'counsellor'] },
    { path: '/offline/batches', label: 'Batches', icon: UsersRound, roles: ['admin', 'manager', 'offline_manager'] },
    { path: '/offline/classes', label: 'Classes', icon: Calendar, roles: ['admin', 'manager', 'offline_manager'] },
    { path: '/offline/reports', label: 'Reports', icon: FileBarChart, roles: ['admin', 'manager', 'offline_manager'] },
  ];

  // Special Events sub-navigation items
  const specialEventsNavItems = [
    { path: '/special-events', label: 'Scheduling', icon: Calendar, roles: ['admin', 'manager', 'special_events_agent'] },
    { path: '/special-events/customization', label: 'Event Library', icon: FileText, roles: ['admin', 'manager', 'special_events_agent'] },
    { path: '/special-events/teacher-stats', label: 'Teacher Stats', icon: FileBarChart, roles: ['admin', 'manager'] },
  ];

  // Admin-only items
  const adminNavItems = [
    { path: '/admin/module-access', label: 'Module Access', icon: Settings, roles: ['admin'] },
    { path: '/admin/password-resets', label: 'Password Resets', icon: Key, roles: ['admin', 'manager'] },
  ];

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/sales/my-performance') return 'My Performance';
    if (path === '/complaints/my-performance') return 'My Performance';
    if (path === '/classes/my-performance') return 'My Performance';
    if (path === '/classes/list') return 'Classes';
    if (path === '/classes/teachers') return 'Teachers';
    if (path === '/sales/dashboard') return 'Sales Dashboard';
    if (path === '/sales/leads') return 'Leads';
    if (path === '/sales/unused-leads') return 'Unused Leads';
    if (path === '/sales/users') return 'Users';
    if (path === '/sales/reminders') return 'Reminders';
    if (path === '/sales/google-sheet-sync') return 'Google Sheet Sync';
    if (path === '/sales/caller-groups') return 'Caller Groups';
    if (path === '/sales/lead-groups') return 'Lead Groups';
    if (path === '/sales/products') return 'Products';
    if (path === '/sales/lead-forms') return 'Lead Forms';
    if (path === '/sales/leaderboard') return 'Sales Leaderboard';
    if (path === '/complaints/dashboard') return 'Complaints Dashboard';
    if (path === '/complaints/new') return 'New Complaint';
    if (path === '/complaints/list') return 'All Complaints';
    if (path === '/complaints/problem-types') return 'Problem Types';
    if (path === '/complaints/public-form') return 'Public Form Settings';
    if (path === '/classes/dashboard') return 'Class Dashboard';
    if (path === '/classes/app') return 'App Classes';
    if (path === '/classes/youtube') return 'YouTube Classes';
    if (path === '/classes/youtube/by-channel') return 'YouTube by Channel';
    if (path === '/classes/list') return 'App Classes';
    if (path === '/classes/subjects') return 'Subjects';
    if (path === '/classes/teachers') return 'Teachers';
    if (path === '/classes/reports') return 'Teacher Performance Report';
    if (path === '/classes/progress') return 'Course Progress';
    // if (path === '/classes/planner') return 'Course Planner'; // Temporarily disabled
    if (path === '/admin/module-access') return 'Module Access';
    if (path === '/admin/password-resets') return 'Password Resets';
    if (path === '/stock/inward-outward') return 'Inward / Outward';
    if (path === '/stock/ledger') return 'Book Ledger';
    if (path === '/stock/status') return 'Stock Status';
    if (path === '/stock/modify') return 'Modify Stock Data';
    if (path === '/content/questions') return 'Question Repository';
    if (path === '/content/books') return 'Book Repository';
    if (path === '/content/subjects') return 'Subjects & Topics';
    if (path === '/content') return 'Content';
    if (path === '/offline/dashboard') return 'Offline Centre Dashboard';
    if (path === '/offline/enquiries') return 'Student Enquiries';
    if (path === '/offline/admissions') return 'Admissions';
    if (path === '/offline/batches') return 'Batches';
    if (path === '/offline/classes') return 'Offline Classes';
    if (path === '/offline/reports') return 'Offline Centre Reports';
    if (path === '/results') return 'Results';
    if (path === '/results/forms') return 'Result Forms';
    if (path === '/ecommerce') return 'E-Commerce';
    if (path === '/marketing' || path.startsWith('/marketing/')) return 'Marketing';
    if (path === '/cashflow' || path.startsWith('/cashflow/')) return 'Cash Flow';
    if (path === '/doubt-engine') return 'Doubt Engine';
    if (path === '/doubt-engine/inbox') return 'My Doubt Inbox';
    if (path === '/teacher-feedback') return 'Teacher Feedback';
    if (path === '/teacher-feedback/mine') return 'My Feedback';
    if (path === '/complaint-chat') return 'Live Complaint Chat';
    if (path === '/complaint-chat/agent') return 'Complaint Chat Inbox';
    return 'Dashboard';
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-30
        w-64 bg-gradient-to-b from-slate-900 to-slate-800 text-white flex flex-col shadow-xl
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo/Brand */}
        <div className="p-6 border-b border-slate-700 flex items-center gap-3">
          <img src="/exampur-logo.png" alt="Exampur" className="w-12 h-12 object-contain" />
          <div>
            <h1 className="text-lg font-bold font-headings uppercase tracking-tight">Exampur</h1>
            <p className="text-xs text-slate-400 font-body">Management Software</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {/* Main Dashboard */}
          {mainNavItems
            .filter(item => item.roles.includes(user?.role))
            .map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                    isActive(item.path)
                      ? 'bg-white text-slate-900 shadow-lg font-semibold'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-body">{item.label}</span>
                </Link>
              );
            })}

          {/* Sales Calling Section (Collapsible) */}
          {(user?.role === 'admin' || user?.role === 'caller' || hasModuleAccess('sales')) && (
            <div className="pt-2">
              <button
                data-testid="nav-sales-calling-toggle"
                onClick={() => setSalesCallingOpen(!salesCallingOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all ${
                  isSalesActive()
                    ? 'bg-emerald-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Headphones size={20} />
                  <span className="font-body font-medium">Sales Calling</span>
                </div>
                {salesCallingOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {/* Sales Calling Sub-items */}
              {salesCallingOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-slate-700 pl-2">
                  {salesNavItems
                    .filter(item => item.roles.includes(user?.role))
                    .map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                            isActive(item.path)
                              ? 'bg-white text-slate-900 shadow-lg font-semibold'
                              : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          <Icon size={16} />
                          <span className="font-body">{item.label}</span>
                        </Link>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Complaints Section (Collapsible) */}
          {(user?.role === 'admin' || ['complaint_entry', 'complaint_solver'].includes(user?.role) || hasModuleAccess('complaints')) && (
            <div className="pt-2">
              <button
                data-testid="nav-complaints-toggle"
                onClick={() => setComplaintsOpen(!complaintsOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all ${
                  isComplaintsActive()
                    ? 'bg-orange-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <MessageSquare size={20} />
                  <span className="font-body font-medium">Complaints</span>
                </div>
                {complaintsOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {/* Complaints Sub-items */}
              {complaintsOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-slate-700 pl-2">
                  {complaintsNavItems
                    .filter(item => item.roles.includes(user?.role))
                    .map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-complaints-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                            isActive(item.path)
                              ? 'bg-white text-slate-900 shadow-lg font-semibold'
                              : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          <Icon size={16} />
                          <span className="font-body">{item.label}</span>
                        </Link>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Class Delivery Section (Collapsible) */}
          {(user?.role === 'admin' || ['class_conduction_agent', 'teacher'].includes(user?.role) || hasModuleAccess('class_delivery')) && (
            <div className="pt-2">
              <button
                data-testid="nav-classes-toggle"
                onClick={() => setClassDeliveryOpen(!classDeliveryOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all ${
                  location.pathname.startsWith('/classes')
                    ? 'bg-purple-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <GraduationCap size={20} />
                  <span className="font-body font-medium">Class Delivery</span>
                </div>
                {classDeliveryOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {/* Class Delivery Sub-items */}
              {classDeliveryOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-slate-700 pl-2">
                  {classDeliveryNavItems
                    .filter(item => item.roles.includes(user?.role))
                    .map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-classes-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                            isActive(item.path)
                              ? 'bg-white text-slate-900 shadow-lg font-semibold'
                              : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          <Icon size={16} />
                          <span className="font-body">{item.label}</span>
                        </Link>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Typing Module Section (Collapsible) */}
          {(user?.role === 'admin' || user?.role === 'typing_manager' || user?.role === 'typing_agent' || user?.role === 'teacher' || hasModuleAccess('typing')) && (
            <div className="pt-2">
              <button
                data-testid="nav-typing-toggle"
                onClick={() => setTypingOpen(!typingOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all ${
                  location.pathname.startsWith('/typing')
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <FileText size={20} />
                  <span className="font-body font-medium">Typing</span>
                </div>
                {typingOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {/* Typing Sub-items */}
              {typingOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-slate-700 pl-2">
                  {/* Typing Dashboard - For managers only */}
                  {(user?.role === 'admin' || user?.role === 'typing_manager' || (user?.role === 'manager' && hasModuleAccess('typing'))) && (
                    <Link
                      to="/typing"
                      data-testid="nav-typing-dashboard"
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                        location.pathname === '/typing'
                          ? 'bg-white text-slate-900 shadow-lg font-semibold'
                          : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                      }`}
                    >
                      <BarChart3 size={16} />
                      <span className="font-body">Dashboard</span>
                    </Link>
                  )}

                  {/* PPT Repository - For all typing users */}
                  <Link
                    to="/typing/repository"
                    data-testid="nav-typing-repository"
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                      location.pathname === '/typing/repository'
                        ? 'bg-white text-slate-900 shadow-lg font-semibold'
                        : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <Archive size={16} />
                    <span className="font-body">PPT Repository</span>
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Content Module Section (Collapsible) */}
          {(user?.role === 'admin' || user?.role === 'content_manager' || user?.role === 'content_agent' || hasModuleAccess('content')) && (
            <div className="pt-2">
              <button
                data-testid="nav-content-toggle"
                onClick={() => setContentOpen(!contentOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all ${
                  location.pathname.startsWith('/content')
                    ? 'bg-emerald-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <FileText size={20} />
                  <span className="font-body font-medium">Content</span>
                </div>
                {contentOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {/* Content Sub-items */}
              {contentOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-slate-700 pl-2">
                  {/* Question Repository - For all content users */}
                  <Link
                    to="/content/questions"
                    data-testid="nav-content-questions"
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                      location.pathname === '/content/questions'
                        ? 'bg-white text-slate-900 shadow-lg font-semibold'
                        : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <HelpCircle size={16} />
                    <span className="font-body">Question Repository</span>
                  </Link>

                  {/* Book Repository - For all content users */}
                  <Link
                    to="/content/books"
                    data-testid="nav-content-books"
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                      location.pathname === '/content/books'
                        ? 'bg-white text-slate-900 shadow-lg font-semibold'
                        : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <BookOpen size={16} />
                    <span className="font-body">Book Repository</span>
                  </Link>

                  {/* Subjects & Topics - For managers only */}
                  {(user?.role === 'admin' || user?.role === 'content_manager' || (user?.role === 'manager' && hasModuleAccess('content'))) && (
                    <Link
                      to="/content/subjects"
                      data-testid="nav-content-subjects"
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                        location.pathname === '/content/subjects'
                          ? 'bg-white text-slate-900 shadow-lg font-semibold'
                          : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                      }`}
                    >
                      <FolderTree size={16} />
                      <span className="font-body">Subjects & Topics</span>
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Books Stock Section (Collapsible) */}
          {(user?.role === 'admin' || user?.role === 'stock_manager' || hasModuleAccess('books_stock')) && (
            <div className="pt-2">
              <button
                data-testid="nav-books-stock-toggle"
                onClick={() => setBooksStockOpen(!booksStockOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all ${
                  location.pathname.startsWith('/stock')
                    ? 'bg-cyan-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Warehouse size={20} />
                  <span className="font-body font-medium">Books Stock</span>
                </div>
                {booksStockOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {/* Books Stock Sub-items */}
              {booksStockOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-slate-700 pl-2">
                  {booksStockNavItems
                    .filter(item => item.roles.includes(user?.role))
                    .map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-stock-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                            isActive(item.path)
                              ? 'bg-white text-slate-900 shadow-lg font-semibold'
                              : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          <Icon size={16} />
                          <span className="font-body">{item.label}</span>
                        </Link>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* HR Section (Collapsible) - Admin or anyone with HR module access */}
          {(user?.role === 'admin' || hasModuleAccess('hr')) && (
            <div className="pt-2">
              <button
                data-testid="nav-hr-toggle"
                onClick={() => setHrOpen(!hrOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all ${
                  location.pathname.startsWith('/hr')
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Shield size={20} />
                  <span className="font-body font-medium">HR</span>
                </div>
                {hrOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {/* HR Sub-items */}
              {hrOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-slate-700 pl-2">
                  {hrNavItems
                    .filter(item => item.roles.includes(user?.role) || item.roles.includes('manager'))
                    .map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-hr-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                            isActive(item.path)
                              ? 'bg-white text-slate-900 shadow-lg font-semibold'
                              : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          <Icon size={16} />
                          <span className="font-body">{item.label}</span>
                        </Link>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Finance Section (Collapsible) - Admin or anyone with Finance module access */}
          {(user?.role === 'admin' || hasModuleAccess('finance')) && (
            <div className="pt-2">
              <button
                data-testid="nav-finance-toggle"
                onClick={() => setFinanceOpen(!financeOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all ${
                  location.pathname.startsWith('/finance')
                    ? 'bg-amber-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Building2 size={20} />
                  <span className="font-body font-medium">Finance</span>
                </div>
                {financeOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {/* Finance Sub-items */}
              {financeOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-slate-700 pl-2">
                  {financeNavItems
                    .filter(item => item.roles.includes(user?.role) || item.roles.includes('manager'))
                    .map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-finance-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                            isActive(item.path)
                              ? 'bg-white text-slate-900 shadow-lg font-semibold'
                              : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          <Icon size={16} />
                          <span className="font-body">{item.label}</span>
                        </Link>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Offline Centre Section (Collapsible) */}
          {(user?.role === 'admin' || user?.role === 'offline_manager' || user?.role === 'counsellor' || hasModuleAccess('offline_centre')) && (
            <div className="pt-2">
              <button
                data-testid="nav-offline-toggle"
                onClick={() => setOfflineOpen(!offlineOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all ${
                  location.pathname.startsWith('/offline')
                    ? 'bg-teal-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Building2 size={20} />
                  <span className="font-body font-medium">Offline Centre</span>
                </div>
                {offlineOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {/* Offline Centre Sub-items */}
              {offlineOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-slate-700 pl-2">
                  {offlineCentreNavItems
                    .filter(item => item.roles.includes(user?.role?.toLowerCase()) || user?.role === 'admin')
                    .map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-offline-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                            isActive(item.path)
                              ? 'bg-white text-slate-900 shadow-lg font-semibold'
                              : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          <Icon size={16} />
                          <span className="font-body">{item.label}</span>
                        </Link>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Offline Centre Section end */}

          {/* Special Events Section (Collapsible) */}
          {(user?.role === 'admin' || hasModuleAccess('special_events')) && (
            <div className="pt-2">
              <button
                data-testid="nav-special-events-toggle"
                onClick={() => setSpecialEventsOpen(!specialEventsOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all ${
                  location.pathname.startsWith('/special-events')
                    ? 'bg-rose-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Sparkles size={20} />
                  <span className="font-body font-medium">Special Events</span>
                </div>
                {specialEventsOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {specialEventsOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-slate-700 pl-2">
                  {specialEventsNavItems
                    .filter(item => item.roles.includes(user?.role?.toLowerCase()) || user?.role === 'admin' || user?.module_access?.includes('special_events'))
                    .map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-se-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => setSidebarOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                            isActive(item.path)
                              ? 'bg-white text-slate-900 shadow-lg font-semibold'
                              : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          <Icon size={16} />
                          <span className="font-body">{item.label}</span>
                        </Link>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Special Events Section end */}

          {/* Task Manager */}
          {(user?.role === 'admin' || hasModuleAccess('task_manager')) && (
            <div className="pt-2">
              <Link
                to="/tasks"
                data-testid="nav-task-manager"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                  isActive('/tasks')
                    ? 'bg-teal-600 text-white shadow-lg font-semibold'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <ClipboardList size={20} />
                <span className="font-body font-medium">Task Manager</span>
              </Link>
            </div>
          )}

          {/* Results Module */}
          {(user?.role === 'admin' || user?.role === 'result_agent' || hasModuleAccess('results')) && (
            <div className="pt-2">
              <button
                data-testid="nav-results-toggle"
                onClick={() => setResultsOpen(!resultsOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all ${
                  location.pathname.startsWith('/results')
                    ? 'bg-teal-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Trophy size={20} />
                  <span className="font-body font-medium">Results</span>
                </div>
                {resultsOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {resultsOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-slate-700 pl-2">
                  <Link
                    to="/results"
                    data-testid="nav-results"
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                      location.pathname === '/results'
                        ? 'bg-white text-slate-900 shadow-lg font-semibold'
                        : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <Trophy size={16} />
                    <span className="font-body">All Entries</span>
                  </Link>
                  {(user?.role === 'admin' || user?.role === 'manager') && (
                    <Link
                      to="/results/forms"
                      data-testid="nav-result-forms"
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                        location.pathname.startsWith('/results/forms')
                          ? 'bg-white text-slate-900 shadow-lg font-semibold'
                          : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                      }`}
                    >
                      <ClipboardList size={16} />
                      <span className="font-body">Result Forms</span>
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Mark Attendance - Only visible for Team Leaders */}

          {/* E-Commerce Module */}
          {(user?.role === 'admin' || hasModuleAccess('ecommerce')) && (
            <div className="pt-2">
              <Link
                to="/ecommerce"
                data-testid="nav-ecommerce"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                  isActive('/ecommerce')
                    ? 'bg-teal-600 text-white shadow-lg font-semibold'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <Package size={20} />
                <span className="font-body font-medium">E-Commerce</span>
              </Link>
            </div>
          )}

          {/* Marketing Module — WhatsApp Marketing only */}
          {(user?.role === 'admin' || hasModuleAccess('marketing')) && (
            <div className="pt-2">
              <Link
                to="/marketing"
                data-testid="nav-marketing"
                onClick={() => setSidebarOpen(false)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                  location.pathname.startsWith('/marketing')
                    ? 'bg-pink-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <Sparkles size={20} />
                <span className="font-body font-medium">Marketing</span>
              </Link>
            </div>
          )}

          {/* Cash Flow Module (collapsible) */}
          {(user?.role === 'admin' || hasModuleAccess('cash_flow')) && (
            <div className="pt-2">
              <button
                data-testid="nav-cashflow-toggle"
                onClick={() => setCashFlowOpen(!cashFlowOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all ${
                  location.pathname.startsWith('/cashflow')
                    ? 'bg-emerald-600 text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <IndianRupee size={20} />
                  <span className="font-body font-medium">Cash Flow</span>
                </div>
                {cashFlowOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>

              {cashFlowOpen && (
                <div className="mt-1 ml-4 space-y-1 border-l-2 border-slate-700 pl-2">
                  <Link
                    to="/cashflow"
                    data-testid="nav-cashflow-calendar"
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                      location.pathname === '/cashflow'
                        ? 'bg-white text-slate-900 shadow-lg font-semibold'
                        : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <Calendar size={16} />
                    <span className="font-body">Calendar</span>
                  </Link>
                  <Link
                    to="/cashflow/vendors"
                    data-testid="nav-cashflow-vendors"
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                      location.pathname === '/cashflow/vendors'
                        ? 'bg-white text-slate-900 shadow-lg font-semibold'
                        : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <Building2 size={16} />
                    <span className="font-body">Vendors</span>
                  </Link>
                  <Link
                    to="/cashflow/reimbursements"
                    data-testid="nav-cashflow-reimb"
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                      location.pathname === '/cashflow/reimbursements'
                        ? 'bg-white text-slate-900 shadow-lg font-semibold'
                        : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <Receipt size={16} />
                    <span className="font-body">Reimbursements</span>
                  </Link>
                  {(user?.role === 'admin' || user?.is_finance_controller) && (
                    <Link
                      to="/cashflow/consolidated"
                      data-testid="nav-cashflow-consolidated"
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                        location.pathname === '/cashflow/consolidated'
                          ? 'bg-white text-slate-900 shadow-lg font-semibold'
                          : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                      }`}
                    >
                      <FileBarChart size={16} />
                      <span className="font-body">Consolidated</span>
                    </Link>
                  )}
                  {user?.role === 'admin' && (
                    <Link
                      to="/cashflow/audit"
                      data-testid="nav-cashflow-audit"
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                        location.pathname === '/cashflow/audit'
                          ? 'bg-white text-slate-900 shadow-lg font-semibold'
                          : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                      }`}
                    >
                      <FileText size={16} />
                      <span className="font-body">Audit Log</span>
                    </Link>
                  )}
                  {user?.role === 'admin' && (
                    <Link
                      to="/cashflow/admin"
                      data-testid="nav-cashflow-admin"
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md transition-all text-sm ${
                        location.pathname === '/cashflow/admin'
                          ? 'bg-white text-slate-900 shadow-lg font-semibold'
                          : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                      }`}
                    >
                      <Settings size={16} />
                      <span className="font-body">Admin</span>
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Doubt Engine Module */}
          {(user?.role === 'admin' || hasModuleAccess('doubt_engine')) && (
            <div className="pt-2">
              {/* Admin / Manager / doubt_engine_manager → admin dashboard */}
              {['admin', 'manager', 'doubt_engine_manager'].includes(user?.role) && (
                <Link
                  to="/doubt-engine"
                  data-testid="nav-doubt-engine"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                    location.pathname === '/doubt-engine'
                      ? 'bg-emerald-600 text-white shadow-lg font-semibold'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <MessageSquare size={20} />
                  <span className="font-body font-medium">Doubt Engine</span>
                </Link>
              )}
              {/* Teacher → inbox only */}
              {user?.role === 'teacher' && (
                <Link
                  to="/doubt-engine/inbox"
                  data-testid="nav-doubt-inbox"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                    location.pathname === '/doubt-engine/inbox'
                      ? 'bg-emerald-600 text-white shadow-lg font-semibold'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <MessageSquare size={20} />
                  <span className="font-body font-medium">Doubt Inbox</span>
                </Link>
              )}
            </div>
          )}

          {/* Teacher Feedback Module */}
          {(user?.role === 'admin' || hasModuleAccess('teacher_feedback')) && (
            <div className="pt-2">
              {['admin', 'manager', 'feedback_manager'].includes(user?.role) && (
                <Link
                  to="/teacher-feedback"
                  data-testid="nav-teacher-feedback"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                    location.pathname === '/teacher-feedback'
                      ? 'bg-amber-600 text-white shadow-lg font-semibold'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <Star size={20} />
                  <span className="font-body font-medium">Teacher Feedback</span>
                </Link>
              )}
              {user?.role === 'teacher' && (
                <Link
                  to="/teacher-feedback/mine"
                  data-testid="nav-my-feedback"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                    location.pathname === '/teacher-feedback/mine'
                      ? 'bg-amber-600 text-white shadow-lg font-semibold'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <Star size={20} />
                  <span className="font-body font-medium">My Feedback</span>
                </Link>
              )}
            </div>
          )}

          {/* Mark Attendance - Only visible for Team Leaders (original) */}
          {isTeamLeader && (
            <div className="pt-2">
              <Link
                to="/hr/attendance/mark"
                data-testid="nav-mark-attendance"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                  isActive('/hr/attendance/mark')
                    ? 'bg-teal-600 text-white shadow-lg font-semibold'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <CalendarCheck size={20} />
                <span className="font-body font-medium">Mark Attendance</span>
              </Link>
            </div>
          )}

          {/* Admin Items */}
          {adminNavItems
            .filter(item => item.roles.includes(user?.role))
            .map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
                    isActive(item.path)
                      ? 'bg-white text-slate-900 shadow-lg font-semibold'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-body">{item.label}</span>
                </Link>
              );
            })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-4 px-2">
            <UserCircle size={32} className="text-slate-400" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate font-body">{user?.name}</p>
              <p className="text-xs text-slate-400 uppercase font-body">{user?.role}</p>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-md transition-all font-body text-sm"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-slate-100 transition-colors"
              data-testid="mobile-menu-button"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            
            <div>
              <h2 className="text-xl font-semibold font-headings text-slate-900">
                {getPageTitle()}
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <NotificationDropdown />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-slate-50 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
